/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

import { environment } from '../../environments/environment';

/**
 * Cloudflare's documented "always passes" test site key.
 *
 * It issues a dummy token immediately with no challenge and no widget iframe, which
 * is what makes it usable from headless Chromium. The real managed widget refuses to
 * render under automation at all, so any browser-driven test of a Turnstile-protected
 * form is impossible without this.
 *
 * https://developers.cloudflare.com/turnstile/troubleshooting/testing/
 */
export const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA';

/**
 * localStorage flag that opts a browser session into the test key.
 *
 * Set it before loading a Turnstile-protected page:
 *   localStorage.setItem('nexleader_turnstile_test_mode', 'true')
 */
export const TURNSTILE_TEST_MODE_KEY = 'nexleader_turnstile_test_mode';

/**
 * resolveTurnstileSiteKey()
 *
 * Returns the Turnstile site key a widget should render with.
 *
 * Why this exists: the dev server deliberately overrides environment.ts to the REAL
 * managed site key so dev exercises real Turnstile behaviour rather than a stub. That
 * is the right default — but it also means the submit button on the direct signup and
 * password reset forms can never be enabled from an automated browser, because the
 * real widget declines to render for one. That blocked reproducing a reported bug in
 * the direct purchase flow (2026-08-26) and blocks any future browser-level test of
 * the highest-risk form in the product.
 *
 * So: a non-production build may opt a single browser session into Cloudflare's
 * always-pass test key via localStorage. The escape hatch is deliberately narrow.
 *
 *   - The environment.production guard is the first statement, so a production build
 *     returns the real key before the flag is ever read. Leaving the flag set in a
 *     browser that later visits production changes nothing. Note this is a RUNTIME
 *     guard, not dead-code elimination: the constants below do still appear in the
 *     production bundle, they are simply unreachable. Verified against a real
 *     production build 2026-08-26 — it ships `production:!0` and the minified
 *     `if(Je.production)return Je.turnstileSiteKey;` ahead of the override.
 *   - It is a boolean opt-in, not a free-text key. A caller cannot point the widget at
 *     an arbitrary third-party site key; the only alternative is Cloudflare's own
 *     documented test key.
 *
 * Note the server side is separate: TurnstileMiddleware only verifies when
 * config.enableTurnstile is true. A dummy token is accepted while that flag is off
 * (its current state on dev). If it is ever turned on for dev, the matching always-pass
 * SECRET (1x0000000000000000000000000000000AA) has to go in config alongside it, or
 * siteverify will reject the dummy token this returns.
 *
 * @returns {string} the site key to render with
 */
export function resolveTurnstileSiteKey(): string {
    // Production never consults the override. Keep this check first so there is no
    // path in a production bundle that reads the flag at all.
    if (environment.production) return environment.turnstileSiteKey;

    try {
        if (localStorage.getItem(TURNSTILE_TEST_MODE_KEY) === 'true') {
            // Loud on purpose: if this ever shows up in a screenshot of a bug report,
            // whoever is reading knows the form was not exercising real bot protection.
            console.warn(
                '[Turnstile] TEST MODE — using Cloudflare\'s always-pass site key. ' +
                'Bot protection is NOT being exercised. Clear ' +
                `${TURNSTILE_TEST_MODE_KEY} from localStorage to use the real widget.`
            );
            return TURNSTILE_TEST_SITE_KEY;
        }
    } catch (e) {
        // localStorage throws in some privacy modes; fall through to the real key.
    }

    return environment.turnstileSiteKey;
}
